// L'identité d'un client : son NOM, son logo et ses couleurs.
//
// LE NOM Y A REJOINT LE RESTE LE 21/09/2026, et pour la même raison que les
// couleurs trois semaines plus tôt : le serveur acceptait déjà la
// modification (`schemas.ClientUpdate.nom`), il ne manquait que l'écran.
// Renommer un client obligeait à écrire dans la base à la main — ce qui a été
// fait ce jour-là pour « Alpha Course » devenu « Alpha Courses ».
//
// ⚠️ LE NOM CHANGE, LE SLUG JAMAIS. Le slug est l'identifiant : c'est lui
// qu'il faut retaper pour supprimer un client, et c'est ce qui rend cette
// barrière fiable. Il n'est donc pas modifiable ici, et il est affiché à côté
// du nom pour qu'on voie bien que ce sont deux choses différentes.
//
// CE QUE CET ÉCRAN REMPLACE : un déploiement. Jusqu'au 17/08/2026, donner son
// logo à un client demandait de poser une image dans le code du frontend et de
// mettre le logiciel en ligne. Quinze minutes et un risque, à chaque vente —
// et le produit finissait par transporter dans son code les logos de tous ses
// clients.
//
// LES COULEURS Y ONT REJOINT LE LOGO (17/08), et pas seulement par commodité :
// elles ne se réglaient qu'à la CRÉATION d'un client. Une charte saisie de
// travers, ou un client qui refait son identité, obligeait à passer par la
// base. Le serveur acceptait pourtant déjà la modification — il ne manquait
// que l'écran.
//
// Les réunir tient à ce qu'on en fait : on ouvre cette fenêtre pour « habiller
// un client », pas pour « changer une couleur ». Et cela évite un huitième
// bouton sur une ligne d'actions déjà saturée — le septième débordait déjà.
//
// TOUT CELA APPARTIENT À L'ÉDITEUR, comme les modules. C'est une décision de
// produit : un client qui changerait sa charte pourrait rendre son écran
// illisible un vendredi soir, et c'est nous qu'on appellerait.
import { useEffect, useRef, useState } from "react";

import api from "../api";

// Ce que le SERVEUR accepte (voir routes/commun.TAILLE_MAX_LOGO et
// _detecter_type_image). Recopié ici pour prévenir AVANT l'envoi — un fichier
// de 8 Mo refusé après trente secondes d'attente est une mauvaise expérience.
//
// Ce n'est PAS la protection : le serveur revalide tout, aux octets. Si les
// deux venaient à diverger, c'est le serveur qui a raison, et son message
// s'affiche tel quel (voir messageErreur).
const TAILLE_MAX = 2 * 1024 * 1024;
const FORMATS = ["image/jpeg", "image/png", "image/webp"];

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

// Les trois couleurs de la charte. `couleur_secondaire` n'y figure pas : elle
// vaut le blanc partout et sert de fond au logo — l'exposer inviterait à la
// changer, et un fond sombre derrière un logo sombre ne se voit qu'une fois en
// service, chez le client.
const COULEURS = [
  {
    champ: "couleur_primaire",
    titre: "Couleur principale",
    detail: "Le menu de gauche, et les en-têtes.",
  },
  {
    champ: "couleur_accent",
    titre: "Couleur d’accent",
    detail: "Les boutons d’action et les chiffres mis en avant.",
  },
];

export default function IdentiteClient({ client, onFermer, onChange }) {
  const [etat, setEtat] = useState(client);
  const [erreur, setErreur] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [apercu, setApercu] = useState(null);
  const [choisi, setChoisi] = useState(null);
  const champFichier = useRef(null);

  // Les couleurs sont saisies en local puis envoyées à la validation, à la
  // différence des modules qui basculent au clic. Un sélecteur de couleur émet
  // une valeur à CHAQUE mouvement du curseur : envoyer à chaque changement
  // ferait des dizaines de requêtes pour un seul choix.
  const [couleurs, setCouleurs] = useState({
    couleur_primaire: client.couleur_primaire,
    couleur_accent: client.couleur_accent,
  });

  // Le nom suit la même règle que les couleurs : saisi en local, envoyé à la
  // validation. Une requête par frappe renommerait le client une fois par
  // lettre, et l'écran du client changerait sous ses yeux pendant qu'on tape.
  const [nom, setNom] = useState(client.nom);
  const nomNettoye = nom.trim();
  const nomModifie = nomNettoye !== etat.nom && nomNettoye.length > 0;

  const couleursModifiees =
    couleurs.couleur_primaire !== etat.couleur_primaire ||
    couleurs.couleur_accent !== etat.couleur_accent;

  const enregistrerCouleurs = () => {
    setEnCours(true);
    setErreur("");
    api
      .patch(`/plateforme/clients/${client.id}`, couleurs)
      // On repart de la réponse du SERVEUR, jamais de ce qu'on vient
      // d'envoyer : si une valeur est refusée ou corrigée, l'écran doit
      // montrer l'état réel.
      .then(({ data }) => {
        setEtat(data);
        setCouleurs({
          couleur_primaire: data.couleur_primaire,
          couleur_accent: data.couleur_accent,
        });
        onChange?.(data);
      })
      .catch((err) =>
        setErreur(messageErreur(err, "Enregistrement impossible."))
      )
      .finally(() => setEnCours(false));
  };

  const enregistrerNom = () => {
    setEnCours(true);
    setErreur("");
    api
      .patch(`/plateforme/clients/${client.id}`, { nom: nomNettoye })
      // On repart de la réponse du SERVEUR, comme pour les couleurs : si le
      // nom est corrigé ou refusé, l'écran doit montrer l'état réel et non ce
      // qu'on espérait.
      .then(({ data }) => {
        setEtat(data);
        setNom(data.nom);
        onChange?.(data);
      })
      .catch((err) =>
        setErreur(messageErreur(err, "Renommage impossible."))
      )
      .finally(() => setEnCours(false));
  };

  // Un objectURL se révoque, sinon ses octets restent en mémoire à chaque
  // fichier essayé. Même règle que dans marque.js.
  useEffect(() => () => apercu && URL.revokeObjectURL(apercu), [apercu]);

  const choisir = (evenement) => {
    const fichier = evenement.target.files?.[0];
    setErreur("");
    if (!fichier) return;

    // On prévient avant d'envoyer, avec les DEUX chiffres : « trop gros » sans
    // dire de combien oblige à essayer au hasard.
    if (!FORMATS.includes(fichier.type)) {
      setErreur("Format non accepté. Choisissez une image JPEG, PNG ou WebP.");
      return;
    }
    if (fichier.size > TAILLE_MAX) {
      const mo = (fichier.size / (1024 * 1024)).toFixed(1);
      setErreur(`Image trop lourde : ${mo} Mo, pour 2 Mo au maximum.`);
      return;
    }

    if (apercu) URL.revokeObjectURL(apercu);
    setApercu(URL.createObjectURL(fichier));
    setChoisi(fichier);
  };

  const deposer = () => {
    if (!choisi) return;
    const corps = new FormData();
    corps.append("fichier", choisi);
    setEnCours(true);
    setErreur("");
    api
      .post(`/plateforme/clients/${client.id}/logo`, corps)
      // On repart de la réponse du SERVEUR : si le dépôt a été corrigé ou
      // refusé, l'écran doit montrer l'état réel, pas celui qu'on espérait.
      .then(({ data }) => {
        setEtat(data);
        setChoisi(null);
        if (champFichier.current) champFichier.current.value = "";
        onChange?.(data);
      })
      .catch((err) => setErreur(messageErreur(err, "Dépôt impossible.")))
      .finally(() => setEnCours(false));
  };

  const retirer = () => {
    setEnCours(true);
    setErreur("");
    api
      .delete(`/plateforme/clients/${client.id}/logo`)
      .then(({ data }) => {
        setEtat(data);
        onChange?.(data);
      })
      .catch((err) => setErreur(messageErreur(err, "Retrait impossible.")))
      .finally(() => setEnCours(false));
  };

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-titre">Identité du client</h3>
        {/* Le sous-titre suit `etat`, pas `client` : après un renommage il doit
            afficher le nouveau nom sans qu'on referme la fenêtre. */}
        <p className="modal-sous-titre">{etat.nom}</p>

        {erreur && <p className="erreur">{erreur}</p>}

        <h4 className="modal-section">Nom</h4>

        <p className="sous-info">
          Le nom affiché partout chez ce client : son menu, ses écrans, et les
          rapports qu’il produira <strong>à partir de maintenant</strong>. Les
          rapports déjà finalisés gardent le nom qu’ils portaient le jour du
          contrôle — c’est une pièce réglementaire, elle ne se réécrit pas.
        </p>

        <label className="champ">
          <span className="champ-label">Raison sociale</span>
          <input
            type="text"
            value={nom}
            maxLength={150}
            onChange={(e) => setNom(e.target.value)}
            disabled={enCours}
          />
        </label>

        <p className="note-discrete">
          Identifiant technique : <code>{etat.slug}</code> — il ne change pas.
          C’est lui qu’il faut retaper pour supprimer ce client.
        </p>

        <div className="actions-form">
          <button
            className="btn-accent"
            onClick={enregistrerNom}
            disabled={enCours || !nomModifie}
          >
            {enCours ? "Enregistrement…" : "Renommer"}
          </button>
        </div>

        <h4 className="modal-section">Logo</h4>

        <p className="sous-info">
          {etat.logo_televerse
            ? "Ce client a un logo. Il s’affiche dans son menu, à gauche de chaque écran."
            : "Ce client n’a pas de logo : son nom s’affiche en toutes lettres."}
        </p>

        {apercu && (
          <div className="logo-apercu">
            {/* L'APERÇU EST LOCAL, il ne prouve pas que le dépôt a marché.
                Le logo déposé, lui, est servi par une route authentifiée et ne
                peut pas s'afficher dans une simple balise <img> — c'est
                justement le piège de cette fonctionnalité. Montrer un aperçu
                du fichier CHOISI est honnête ; prétendre montrer celui qui est
                en service ne le serait pas. */}
            <img src={apercu} alt="" className="logo-apercu-image" />
            <span className="note-discrete">
              Aperçu du fichier choisi — pas encore déposé.
            </span>
          </div>
        )}

        <label className="champ">
          <span className="champ-label">Choisir une image</span>
          <input
            ref={champFichier}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={choisir}
            disabled={enCours}
          />
        </label>

        <p className="note-discrete">
          JPEG, PNG ou WebP, 2 Mo au maximum. Un logo sur fond transparent
          (PNG) s’intègre mieux au menu, qui prend les couleurs du client.
        </p>

        <h4 className="modal-section">Couleurs</h4>

        {COULEURS.map((c) => (
          <label key={c.champ} className="champ-couleur">
            <input
              type="color"
              value={couleurs[c.champ]}
              disabled={enCours}
              onChange={(e) =>
                setCouleurs((v) => ({ ...v, [c.champ]: e.target.value }))
              }
            />
            <span>
              <strong>{c.titre}</strong>
              <br />
              <span className="note-discrete">{c.detail}</span>
            </span>
          </label>
        ))}

        {/* APERÇU DE CE QUE LE CLIENT VERRA, pas un carré de couleur.
            Un sélecteur affiche la teinte hors de tout contexte ; c'est le
            contraste entre le menu et le bouton qui rend un écran lisible ou
            non, et il ne se juge qu'en voyant les deux ensemble. */}
        <div
          className="couleurs-apercu"
          style={{ background: couleurs.couleur_primaire }}
        >
          <span className="couleurs-apercu-nom">{client.nom}</span>
          <span
            className="couleurs-apercu-bouton"
            style={{ background: couleurs.couleur_accent }}
          >
            Enregistrer
          </span>
        </div>

        {couleursModifiees && (
          <div className="modal-actions">
            <button
              className="btn"
              disabled={enCours}
              onClick={() =>
                setCouleurs({
                  couleur_primaire: etat.couleur_primaire,
                  couleur_accent: etat.couleur_accent,
                })
              }
            >
              Annuler
            </button>
            <button
              className="btn-accent"
              onClick={enregistrerCouleurs}
              disabled={enCours}
            >
              {enCours ? "Enregistrement…" : "Enregistrer les couleurs"}
            </button>
          </div>
        )}

        <p className="note-discrete">
          Les utilisateurs du client verront les nouvelles couleurs à leur
          prochain chargement de page — leur session reste ouverte.
        </p>

        <div className="modal-actions">
          {etat.logo_televerse && (
            <button className="btn" onClick={retirer} disabled={enCours}>
              Retirer le logo
            </button>
          )}
          <button
            className="btn-accent"
            onClick={deposer}
            disabled={enCours || !choisi}
          >
            {enCours ? "Envoi…" : etat.logo_televerse ? "Remplacer" : "Déposer"}
          </button>
          <button className="btn" onClick={onFermer} disabled={enCours}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
